import React, { useState, useEffect } from "react";
import {
  ChevronDown,
  Upload,
  MoreVertical,
  TrendingUp,
  TrendingDown,
  Filter,
  FileText,
  Download,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const AnalyticsReports = () => {
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState("24 hours");

  // Mock data
  const revenueData = [
    { month: "Jan", value: 45 },
    { month: "Feb", value: 52 },
    { month: "Mar", value: 95 },
    { month: "Apr", value: 85 },
    { month: "May", value: 70 },
    { month: "Jun", value: 90 },
    { month: "Jul", value: 75 },
    { month: "Aug", value: 60 },
    { month: "Sep", value: 85 },
    { month: "Oct", value: 95 },
    { month: "Nov", value: 88 },
    { month: "Dec", value: 92 },
  ];

  const dealSizeData = [
    { name: "Small", value: 35, color: "#F97316" },
    { name: "Medium", value: 40, color: "#FB923C" },
    { name: "Large", value: 25, color: "#427BCA" },
  ];

  const stats = [
    { title: "Total Revenue", value: "£248k", change: 5, isPositive: true },
    { title: "New Customers", value: "£275k", change: 8, isPositive: true },
    { title: "Growth Rate", value: "24.5%", change: 2, isPositive: true },
    { title: "Average Deal", value: "£6,250", change: 3, isPositive: false },
  ];

  const leadSourceData = [
    {
      source: "User Registration",
      conversion: "38%",
      revenue: "£173k",
    },
    {
      source: "Feedback Submission",
      conversion: "42%",
      revenue: "£150k",
    },
    {
      source: "Login Form",
      conversion: "30%",
      revenue: "£73k",
    },
    {
      source: "Contact Us",
      conversion: "65%",
      revenue: "£108k",
    },
    {
      source: "Newsletter Sign-Up",
      conversion: "75%",
      revenue: "£220k",
    },
  ];

  const supportPerformanceData = [
    {
      metric: "First Response",
      current: "21m",
      target: "4h",
    },
    {
      metric: "Resolution",
      current: "6.3h",
      target: "24h",
    },
    {
      metric: "CSAT Score",
      current: "92%",
      target: "89%",
    },
    {
      metric: "Tickets per month",
      current: "143",
      target: "-",
    },
    {
      metric: "Backlog",
      current: "12",
      target: "< 10",
    },
  ];

  const recentReports = [
    {
      icon: "📊",
      title: "Monthly Sales Performance",
      generatedBy: "Steve Williams",
      generatedTime: "Today",
    },
    {
      icon: "📈",
      title: "Lead Conversion Analysis",
      generatedBy: "Steve Williams",
      generatedTime: "2d ago",
    },
    {
      icon: "🎯",
      title: "Support SLA Compliance",
      generatedBy: "Steve Williams",
      generatedTime: "1w ago",
    },
  ];

  useEffect(() => {
    setLoading(false);
  }, []);

  // Stats Card Component
  const StatsCard = ({ title, value, change, isPositive }) => (
    <div className="bg-[#13161B] rounded-xl border border-gray-700/50 p-6 relative overflow-hidden group hover:border-gray-600/50 transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="text-[#94979C] text-sm font-medium">{title}</div>
        <button className="text-[#94979C] hover:text-white transition-colors opacity-0 group-hover:opacity-100">
          <MoreVertical size={18} />
        </button>
      </div>

      <div className="flex items-end justify-between">
        <div className="text-3xl font-bold text-white">{value}</div>
        <div
          className={`flex items-center gap-1 text-sm font-semibold ${
            isPositive ? "text-green-500" : "text-red-500"
          }`}
        >
          {isPositive ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
          <span>{change}%</span>
        </div>
      </div>
    </div>
  );

  // Filter Button Component
  const FilterButton = ({ label, isActive, onClick }) => (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
        isActive
          ? "bg-[#0C0E12] text-white border border-gray-700"
          : "text-[#94979C] hover:text-white hover:bg-[#0C0E12]"
      }`}
    >
      {label}
    </button>
  );

  // Dropdown Filter Component
  const DropdownFilter = ({ label }) => (
    <button className="flex items-center gap-2 px-4 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-[#94979C] hover:text-white hover:border-gray-600 transition-colors text-sm font-medium">
      {label}
      <ChevronDown size={16} />
    </button>
  );

  // Revenue Chart Component
  const RevenueChart = () => (
    <div className="bg-[#13161B] rounded-xl border border-gray-700/50 p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white mb-1">
            Revenue Trend
          </h3>
          <p className="text-sm text-[#94979C]">Actual vs Target</p>
        </div>
        <button className="text-[#94979C] hover:text-white transition-colors">
          <MoreVertical size={18} />
        </button>
      </div>

      <ResponsiveContainer width="100%" height={250}>
        <AreaChart data={revenueData}>
          <defs>
            <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#427BCA" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#427BCA" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#22262F" />
          <XAxis
            dataKey="month"
            stroke="#94979C"
            tick={{ fill: "#94979C", fontSize: 12 }}
          />
          <YAxis
            stroke="#94979C"
            tick={{ fill: "#94979C", fontSize: 12 }}
            label={{
              value: "Revenue (k)",
              angle: -90,
              position: "insideLeft",
              fill: "#94979C",
              fontSize: 12,
            }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#13161B",
              border: "1px solid #22262F",
              borderRadius: "8px",
              color: "#fff",
            }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke="#427BCA"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorRevenue)"
          />
        </AreaChart>
      </ResponsiveContainer>

      <div className="grid grid-cols-3 gap-6 mt-6 pt-6 border-t border-[#22262F]">
        <div>
          <div className="text-[#94979C] text-sm mb-1">Actual Revenue</div>
          <div className="text-2xl font-bold text-white">£120k</div>
        </div>
        <div>
          <div className="text-[#94979C] text-sm mb-1">Target Revenue</div>
          <div className="text-2xl font-bold text-white">£100k</div>
        </div>
        <div>
          <div className="text-[#94979C] text-sm mb-1">Vs previous period</div>
          <div className="text-2xl font-bold text-green-500">20%</div>
        </div>
      </div>
    </div>
  );

  // Deal Size Pie Chart Component
  const DealSizeChart = () => (
    <div className="bg-[#13161B] rounded-xl border border-gray-700/50 p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white mb-1">
            Deal Size by Product
          </h3>
        </div>
        <button className="text-[#94979C] hover:text-white transition-colors">
          <MoreVertical size={18} />
        </button>
      </div>

      <div className="flex items-center justify-center">
        <ResponsiveContainer width={200} height={200}>
          <PieChart>
            <Pie
              data={dealSizeData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={80}
              paddingAngle={5}
              dataKey="value"
            >
              {dealSizeData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="space-y-3 mt-6">
        {dealSizeData.map((item, index) => (
          <div key={index} className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-[#94979C] text-sm">{item.name}</span>
            </div>
            <span className="text-white font-semibold">{item.value}%</span>
          </div>
        ))}
      </div>
    </div>
  );

  // Wireless Analysis Component (Circular Progress)
  const WirelessAnalysis = () => (
    <div className="bg-[#13161B] rounded-xl border border-gray-700/50 p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white mb-1">
            Wireless Analysis
          </h3>
        </div>
        <button className="text-[#94979C] hover:text-white transition-colors">
          <MoreVertical size={18} />
        </button>
      </div>

      <div className="flex items-center justify-center py-8">
        <div className="relative">
          <svg width="200" height="200" className="transform -rotate-90">
            {/* Background circle */}
            <circle
              cx="100"
              cy="100"
              r="80"
              fill="none"
              stroke="#22262F"
              strokeWidth="20"
            />
            {/* Progress circle - 64% */}
            <circle
              cx="100"
              cy="100"
              r="80"
              fill="none"
              stroke="#427BCA"
              strokeWidth="20"
              strokeDasharray={`${2 * Math.PI * 80 * 0.64} ${2 * Math.PI * 80}`}
              strokeLinecap="round"
            />
            {/* Progress circle - 36% */}
            <circle
              cx="100"
              cy="100"
              r="80"
              fill="none"
              stroke="#6B9FE0"
              strokeWidth="20"
              strokeDasharray={`${2 * Math.PI * 80 * 0.36} ${2 * Math.PI * 80}`}
              strokeDashoffset={`-${2 * Math.PI * 80 * 0.64}`}
              strokeLinecap="round"
            />
          </svg>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-[#427BCA]" />
            <span className="text-white font-semibold text-lg">64%</span>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-[#6B9FE0]" />
            <span className="text-white font-semibold text-lg">36%</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Table Component
  const DataTable = ({ title, columns, data }) => (
    <div className="bg-[#13161B] rounded-xl border border-gray-700/50 overflow-hidden">
      <div className="flex items-center justify-between p-6 border-b border-[#22262F]">
        <h3 className="text-lg font-semibold text-white">{title}</h3>
        <button className="flex items-center gap-2 px-3 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-[#94979C] hover:text-white hover:border-gray-600 transition-colors text-sm font-medium">
          <Upload size={16} />
          Export
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[#0C0E12]">
            <tr>
              {columns.map((col, index) => (
                <th
                  key={index}
                  className="text-left py-3 px-6 text-[#94979C] font-medium text-sm"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className="border-b border-[#22262F] hover:bg-gray-800/30 transition-colors"
              >
                {Object.values(row).map((value, cellIndex) => (
                  <td key={cellIndex} className="py-4 px-6 text-white text-sm">
                    {value}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Recent Reports Component
  const RecentReportItem = ({ report }) => (
    <div className="flex items-center justify-between py-4 border-b border-[#22262F] last:border-0 hover:bg-gray-800/30 transition-colors group">
      <div className="flex items-center gap-4 flex-1">
        <div className="w-10 h-10 rounded-full bg-[#0C0E12] flex items-center justify-center text-2xl">
          {report.icon}
        </div>
        <div className="flex-1">
          <div className="text-white font-medium mb-1">{report.title}</div>
          <div className="text-[#94979C] text-sm">{report.generatedTime}</div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-[#22262F] rounded-full">
          <div className="w-6 h-6 rounded-full bg-[#427BCA] flex items-center justify-center">
            <span className="text-white text-xs font-semibold">SW</span>
          </div>
          <span className="text-[#94979C] text-sm">{report.generatedBy}</span>
        </div>
        <button className="text-[#94979C] hover:text-white transition-colors opacity-0 group-hover:opacity-100">
          <MoreVertical size={18} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0B0F] text-white font-inter">
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 text-white">
              Analytics & Reports
            </h1>
            <p className="text-sm sm:text-base text-[#94979C] leading-relaxed">
              Track performance across sales, teams, and revenue.
            </p>
          </div>

          <button className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm sm:text-base font-medium">
            <Upload size={16} className="shrink-0" />
            <span className="whitespace-nowrap">Export</span>
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <FilterButton label="24 hours" isActive={true} />
          <FilterButton label="7 days" isActive={false} />
          <FilterButton label="30 days" isActive={false} />
          <FilterButton label="24 hours" isActive={false} />
          <DropdownFilter label="Team" />
          <DropdownFilter label="Sales Stage" />

          <button className="ml-auto flex items-center gap-2 px-4 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-[#94979C] hover:text-white hover:border-gray-600 transition-colors text-sm font-medium">
            <Filter size={16} />
            Apply Filters
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {stats.map((stat, index) => (
            <StatsCard key={index} {...stat} />
          ))}
        </div>

        {/* Revenue Chart */}
        <div className="mb-6">
          <RevenueChart />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <DealSizeChart />
          <WirelessAnalysis />
        </div>

        {/* Tables Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <DataTable
            title="Lead Sources"
            columns={["Sources", "Conversion", "Revenue"]}
            data={leadSourceData}
          />
          <DataTable
            title="Support Performance"
            columns={["Metric", "Current", "Target"]}
            data={supportPerformanceData}
          />
        </div>

        {/* Chat/Support Widget Placeholder */}
        <div className="bg-[#13161B] rounded-xl border border-gray-700/50 p-12 mb-6 flex items-center justify-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-white/20" />
          </div>
        </div>

        {/* Recent Reports */}
        <div className="bg-[#13161B] rounded-xl border border-gray-700/50">
          <div className="flex items-center justify-between p-6 border-b border-[#22262F]">
            <h3 className="text-lg font-semibold text-white">Recent Reports</h3>
            <div className="flex gap-3">
              <button className="flex items-center gap-2 px-3 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-[#94979C] hover:text-white hover:border-gray-600 transition-colors text-sm font-medium">
                <Download size={16} />
                Export
              </button>
              <button
                className="flex items-center justify-center gap-2 px-4 py-2 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] text-sm"
                style={{
                  background:
                    "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                  border: "1px solid",
                  borderImageSource:
                    "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                  boxShadow: `
                    0px 36px 32px -16px #427BCA36,
                    0px 8px 16px 0px #2F578F1F,
                    0px 2px 4px -8px #427BCA14
                  `,
                }}
              >
                <FileText size={16} />
                New Report
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="space-y-2">
              <div className="flex text-sm font-medium mb-4">
                <div className="flex-1 text-[#94979C]">Summary</div>
                <div className="w-32 text-[#94979C]">Generated</div>
                <div className="w-32 text-[#94979C]">By</div>
              </div>
              {recentReports.map((report, index) => (
                <RecentReportItem key={index} report={report} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsReports;
